import { Tooltip } from '@clickcease/cc-client-components-lib'
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { BadBotIcon } from '../../../../../../assets/icons/BadBotIcon'
import { KnownCrawlerIcon } from '../../../../../../assets/icons/KnownCrawlerIcon'
import { ValidVisitIcon } from '../../../../../../assets/icons/ValidVisitIcon'
import { SummaryEntry } from '../../../../../../hooks/api/BMDataTypes'
import { formatNumber } from 'Utils/number'

import {
  SummaryWidgetCardWrapper,
  StatBox,
  StatTitle,
  StatTitleRow,
  StatValue,
  StatPercentage,
  IconCircle,
  TooltipStyle,
} from '../SummaryWidget.styled'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

interface SummaryWidgetCardProps {
  summaryData: SummaryEntry | null
}

interface StatBoxItemProps {
  title: string
  description: string
  icon: React.ReactNode
  value: number
  percentage: number
  percentageSuffix: string
}

const StatBoxItem: React.FC<StatBoxItemProps> = ({
  title,
  description,
  icon,
  value,
  percentage,
  percentageSuffix,
}) => {
  return (
    <StatBox>
      <StatTitle>
        <StatTitleRow>
          {title}
          <Tooltip
            tooltiptContent={description}
            constentStyle={{ width: '270px', fontSize: '12px' }}
            side="top"
          >
            <FontAwesomeIcon icon={faInfoCircle as IconProp} style={TooltipStyle} />
          </Tooltip>
        </StatTitleRow>
        <IconCircle>{icon}</IconCircle>
      </StatTitle>
      <Tooltip tooltiptContent={value} side="top">
        <StatValue>{formatNumber(value)}</StatValue>
      </Tooltip>
      <StatPercentage>
        {percentage.toFixed(2)} {percentageSuffix}
      </StatPercentage>
    </StatBox>
  )
}

export const SummaryWidgetCards: React.FC<SummaryWidgetCardProps> = ({ summaryData }) => {
  const { t } = useTranslation('botMitigation')

  const statBoxData = [
    {
      title: t('SummaryWidget.sections.protectedValidVisits.title'),
      description: t('SummaryWidget.sections.protectedValidVisits.description'),
      icon: <ValidVisitIcon />,
      value: summaryData?.protectedValidVisits || 0,
      percentage: summaryData?.protectedValidVisitsPercentage,
      percentageSuffix: t('SummaryWidget.sections.percentOfAllVisits'),
    },
    {
      title: t('SummaryWidget.sections.badBotsAndInvalidRequests.title'),
      description: t('SummaryWidget.sections.badBotsAndInvalidRequests.description'),
      icon: <BadBotIcon />,
      value: summaryData?.totalBlocked || 0,
      percentage: summaryData?.invalidRate,
      percentageSuffix: t('SummaryWidget.sections.percentOfAllVisits'),
    },
    {
      title: t('SummaryWidget.sections.knownCrawlers.title'),
      description: t('SummaryWidget.sections.knownCrawlers.description'),
      icon: <KnownCrawlerIcon />,
      value: summaryData?.whiteListed || 0,
      percentage: summaryData?.whiteListedPercentage,
      percentageSuffix: t('SummaryWidget.sections.percentOfAllVisits'),
    },
  ]

  return (
    <SummaryWidgetCardWrapper>
      {statBoxData.map((data, index) => (
        <StatBoxItem
          key={index}
          title={data.title}
          description={data.description}
          icon={data.icon}
          value={data.value}
          percentage={data.percentage || 0}
          percentageSuffix={data.percentageSuffix}
        />
      ))}
    </SummaryWidgetCardWrapper>
  )
}
